import { MARKET_PLACE } from '@sellsen/contants';
import { GetStorefrontScansLocationEnum } from '@sellsen/generated-sources';
import { t } from 'i18next';
import { SOURCE_CATEGORY_ROOTS } from '../constants/product-categories';

export const convertEnumToArray = (
  enumObj: Record<string, string | number>,
  options?: { translationKeyPrefix?: string }
): { label: string; value: string | number }[] => {
  const prefix = options?.translationKeyPrefix;
  return Object.entries(enumObj).map(([key, value]) => {
    const v = value as string;
    return {
      label: prefix ? t(`${prefix}.${v}`) : t(v),
      value: value,
    };
  });
};

/** Uygulama içi sipariş detayı. Pazar query param yeni sekmede doğru pazarı seçtirir. */
export function getSellsenOrderHref(
  amazonOrderId: string | undefined,
  marketplace?: string
): string | undefined {
  const id = amazonOrderId?.trim();
  if (!id) {
    return undefined;
  }
  const path = `/orders/${encodeURIComponent(id)}`;
  const mp = marketplace?.trim();
  return mp ? `${path}?marketplace=${encodeURIComponent(mp)}` : path;
}

export function openSellsenOrder(
  amazonOrderId: string | undefined,
  marketplace?: string
): void {
  const href = getSellsenOrderHref(amazonOrderId, marketplace);
  if (!href) {
    return;
  }
  window.open(href, '_blank', 'noopener,noreferrer');
}

/** Amazon.com ana kategori adları. Canlı kaynak kayıtlarından üretilen sabit liste. */
export const getProductCategoryOptions = (): {
  label: string;
  value: string;
}[] => SOURCE_CATEGORY_ROOTS;

/**
 * Alt kategori zinciri. Boşsa undefined: liste hücresi kesilen ana kategoriyi
 * tooltip'te göstermeye devam eder.
 */
export function categoryLeafTooltip(
  leafCategory: string[] | null | undefined
): string | undefined {
  const leaves = (leafCategory ?? [])
    .map((leaf) => leaf.trim())
    .filter((leaf) => leaf.length > 0);
  return leaves.length > 0 ? leaves.join(' › ') : undefined;
}

export const getAvailableMarketPlaces = () => {
  return MARKET_PLACE.filter(
    (marketPlace) =>
      marketPlace.name !== 'UNITED_STATES' &&
      marketPlace.name !== 'BRAZIL' &&
      marketPlace.name !== 'EGYPT' &&
      marketPlace.name !== 'INDIA' &&
      marketPlace.name !== 'SINGAPORE'
  );
};

export const getCurrency = (countryCode: string) => {
  const market = MARKET_PLACE.find((market) => market.id === countryCode);
  return market?.currencyCode || 'USD';
};

/** ISO ülke kodu (örn. DE, US, GB) → marketplace para birimi */
export const getCurrencyByIsoCountryCode = (isoCode?: string) => {
  if (!isoCode) return 'USD';
  const market = MARKET_PLACE.find(
    (m) => m.countryCode.toUpperCase() === isoCode.toUpperCase()
  );
  return market?.currencyCode ?? 'USD';
};

export const getCountryInformation = (countryCode: string) => {
  return MARKET_PLACE.find((market) => market.id === countryCode);
};

/**
 * Bir satırın hangi marketplace'e ait olduğunu çözer: önce siparişin kendi
 * marketplace'i, o yoksa (veya `ALL` ise) listenin üstündeki seçili ülke.
 */
export function resolveRowMarketplaceId(
  orderMarketplace: string | undefined,
  listMarketplaceFallback: string | undefined
): string | null {
  const mp = orderMarketplace?.trim();
  if (mp && mp !== 'ALL') return mp;

  const fallback = listMarketplaceFallback?.trim();
  if (!fallback || fallback === 'ALL') return null;
  return fallback;
}

/**
 * Seller Central sipariş URL’i. Perakende `MarketPlaceType.domain` ile
 * `https://sellercentral.amazon.{domain}/orders-v3/order/{id}` üretilir.
 * `IRELAND` listede yok; UK Seller Central kullanılır.
 */
export function getSellerCentralOrderUrl(
  amazonOrderId: string | undefined,
  orderMarketplace: string | undefined,
  listMarketplaceFallback: string | undefined
): string | null {
  const id = amazonOrderId?.trim();
  if (!id) return null;

  const mp = resolveRowMarketplaceId(orderMarketplace, listMarketplaceFallback);
  if (!mp) return null;

  const marketId = mp === 'IRELAND' ? 'UNITED_KINGDOM' : mp;
  const market = MARKET_PLACE.find((m) => m.id === marketId);
  if (!market) return null;

  const base = `https://sellercentral.amazon.${market.domain}`;
  return `${base}/orders-v3/order/${encodeURIComponent(id)}`;
}

/**
 * Perakende Amazon ürün URL’i (`https://www.amazon.{domain}/dp/{asin}`).
 * Ülke sabit değildir: siparişin kendi marketplace'i, yoksa listedeki seçili
 * ülke kullanılır. Menü etiketinde göstermek için ISO ülke kodu da döner.
 */
export function getAmazonProductUrl(
  asin: string | undefined,
  orderMarketplace: string | undefined,
  listMarketplaceFallback: string | undefined
): { url: string; countryCode: string } | null {
  const code = asin?.trim();
  if (!code) return null;

  const marketId = resolveRowMarketplaceId(
    orderMarketplace,
    listMarketplaceFallback
  );
  if (!marketId) return null;

  const market = MARKET_PLACE.find((m) => m.id === marketId);
  if (!market) return null;

  return {
    url: `https://www.amazon.${market.domain}/dp/${encodeURIComponent(code)}`,
    countryCode: market.countryCode,
  };
}

/**
 * Amazon US perakende ürün URL’i. Kaynak alış her zaman amazon.com.
 */
export function getAmazonUsProductUrl(asin: string | undefined): string | null {
  const code = asin?.trim();
  if (!code) return null;
  return `https://www.amazon.com/dp/${encodeURIComponent(code)}`;
}

export const getStoreFrontCountries = (includeAll = false) => {
  return convertEnumToArray(GetStorefrontScansLocationEnum).filter((item) =>
    !includeAll ? item.value !== GetStorefrontScansLocationEnum.All : true
  );
};

const SHIPPING_CARRIER_SHORT: Record<string, string> = {
  APC: 'APC',
  Asendia: 'Asend',
  'DHL Express': 'DHL',
  'FedEx Connect Plus': 'FedEx',
  'FedEx International Priority': 'FedEx',
};

/** Seçilen taşıyıcının en fazla 5 karakterlik kısaltması. Bilinmeyen ad kesilir. */
export function shortShippingCarrier(
  name?: string | null
): string | undefined {
  if (!name) return undefined;
  return SHIPPING_CARRIER_SHORT[name] ?? name.slice(0, 5);
}

/** Maliyet kutusundaki kargo satırı: "Kargo" ya da "Kargo · DHL". */
export function shippingCostLabel(name?: string | null): string {
  const short = shortShippingCarrier(name);
  return short ? `Kargo · ${short}` : 'Kargo';
}

export function calculateProfitMargin(
  profitUsd: number | null | undefined,
  totalCostUsd: number | null | undefined
): number | null {
  if (profitUsd == null || totalCostUsd == null || totalCostUsd === 0) {
    return null;
  }
  return profitUsd / totalCostUsd;
}

/**
 * Depo ücretini ShippingLot'un para biriminde biçimlendirir. Para birimi bilinmiyorsa USD
 * varsayılır — backend'deki `ShippingLotCostConverter.toUsd` de aynı kabulü yapar, iki taraf
 * aynı şeyi göstermeli. (Prod verisinde bu alan bugün hiç dolmuyor; ShippingLot rate yanıtı
 * `currency` göndermiyor.)
 */
export const formatWarehouseFee = (amount?: number, currency?: string) => {
  if (amount == null) {
    return '-';
  }
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency?.trim() || 'USD',
  }).format(amount);
};

/** Amazon.com alıcı hesabı sipariş detayı. Kaynak alış her zaman amazon.com. */
export function getAmazonBuyerOrderUrl(
  buyerOrderId: string | undefined
): string | null {
  const id = buyerOrderId?.trim();
  if (!id) {
    return null;
  }
  return `https://www.amazon.com/gp/your-account/order-details?orderID=${encodeURIComponent(
    id
  )}`;
}

/**
 * ShippingLot şirket adı → resmi takip sayfası. Tam ad veya önek yeter
 * (`FedEx`, `FedEx Connect Plus`). Eşleşme yoksa link üretilmez.
 */
function carrierTrackingUrl(
  carrierName: string,
  encoded: string
): string | null {
  const key = carrierName.trim().toLowerCase();
  if (key === 'apc' || key.startsWith('apc ')) {
    return `https://tracking.apc-pli.com/${encoded}`;
  }
  if (key === 'asendia' || key.startsWith('asendia ')) {
    // track.asendia.com ABD çıkışlı e-PAQ numarasında boş kalıyor; tarama Asendia USA'da.
    return `https://a1.asendiausa.com/tracking/?trackingnumber=${encoded}`;
  }
  if (key === 'dhl' || key.startsWith('dhl ')) {
    return `https://www.dhl.com/en/express/tracking.html?AWB=${encoded}&brand=DHL`;
  }
  if (key === 'fedex' || key.startsWith('fedex ')) {
    return `https://www.fedex.com/fedextrack/?tracknumbers=${encoded}`;
  }
  return null;
}

export function getPackageTrackingUrl(
  trackingNumber: string | undefined,
  trackingUrl: string | undefined,
  carrierName: string | undefined
): string | null {
  const stored = trackingUrl?.trim();
  if (stored) {
    return stored;
  }
  const number = trackingNumber?.trim();
  if (!number || !carrierName?.trim()) {
    return null;
  }
  return carrierTrackingUrl(carrierName, encodeURIComponent(number));
}

export {
  chargeableLbsFrom,
  desiLbsFromCm,
  resolveOrderItemWeights,
} from './volumetric-weight';
export type {
  OrderItemWeightFields,
  ResolvedItemWeights,
} from './volumetric-weight';
